import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';

const Globals = require('../../../app/globals');
const GoogleClassroomActions = require('../actions/GoogleClassroomActions');
const GoogleClassroomStore = require('../stores/GoogleClassroomStore');

// Google Classroom API: https://developers.google.com/classroom/quickstart/js

const CLIENT_ID = Globals.GAPI_CLIENT_ID;
const API_KEY = Globals.GAPI_API_KEY;
const DISCOVERY_DOCS = ["https://www.googleapis.com/discovery/v1/apis/classroom/v1/rest"];

const propTypes = {
    scope: PropTypes.array.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onReject: PropTypes.func,
};

const defaultProps = {
    onReject: () => { return; },
};

// Checks google classroom authorization. Shows a dialog asking for it if necessary.
// calls onSuccess after loading gapi client or onReject if it cannot proceed.
class Authinator extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            showDialog: false,
            showAuthorizeButton: false,
            authorizeButtonText: 'Sign In / Authorize',
            showRejectButton: false,
            showAccountButton: false,
            isSignedIn: false,
            showSignOutButton: false,
            bodyText: '',
            userProfile: null,
            _gapi_state: GoogleClassroomStore.getState()._gapi_state,
            _sign_in_clicked: GoogleClassroomStore.getState()._sign_in_clicked, // we must do this once at least
        };

        this.checkAuth = this.checkAuth.bind(this);
        this.initClient = this.initClient.bind(this);
        this.updateSigninStatus = this.updateSigninStatus.bind(this);
        this.handleSignOut = this.handleSignOut.bind(this);
        this.requestUserProfile = this.requestUserProfile.bind(this);
        this.handleAuthClick = this.handleAuthClick.bind(this);
        this.handleRejectClick = this.handleRejectClick.bind(this);
        this.handleSuccess = this.handleSuccess.bind(this);

        this.unsubscribes = [
            GoogleClassroomStore.listen(this.onGoogleClassroomStoreStateChange.bind(this)),
        ];
    }

    componentDidMount() {
        console.log('Authinator componentDidMount _gapi_state', this.state._gapi_state);
        this.checkAuth();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.checkAuth();
    }
    
    componentWillUnmount() {
        this.unsubscribes.forEach(u => u());
    }

    onGoogleClassroomStoreStateChange(newState) {
        if (newState._gapi_state !== this.state._gapi_state) {
            this.setState({
                _gapi_state: newState._gapi_state,
            }, () => {
                this.checkAuth();
            });
        }

        if (newState._sign_in_clicked !== this.state._sign_in_clicked) {
            this.setState(({
                _sign_in_clicked: newState._sign_in_clicked,
            }), () => {
                if (this.state._sign_in_clicked) { // user signed in
                    const alreadySignedIn = this.state.isSignedIn;
                    gapi.auth2
                        .getAuthInstance()
                        .signIn()
                        .then(() => {
                            if (alreadySignedIn && this.state.isSignedIn) { // TODO: I'm not sure what this used to do but it's now testing the same condition twice
                                this.handleSuccess(); // call this here because updateSigninStatus won't get called if we weren't signed out
                            }
                        });
                }
                else { // user signed out
                    gapi.auth2.getAuthInstance().signOut();
                }

            })
        }
    }

    // invisible check
    checkAuth() {
        console.log('Authinator checkAuth this.state._gapi_state', this.state._gapi_state);
        switch (this.state._gapi_state) {
            case Globals.GAPI_LoadState.START:
                console.log("waiting for gapi load");
                // script hasn't loaded yet
                this.setState({
                    showDialog: true, // stop being invisible
                    bodyText: 'Loading script...'
                });
                break;
                
            case Globals.GAPI_LoadState.CLIENT_LOADED:
                gapi.load('client:auth2', this.initClient);
                break;
                
            case Globals.GAPI_LoadState.CLIENT_INITIALIZED:
                // initialize the client with the specified scope
                gapi.client.init({
                    apiKey: API_KEY,
                    clientId: CLIENT_ID,
                    discoveryDocs: DISCOVERY_DOCS,
                    scope: this.props.scope.join(' ')
                })
                .then(
                    () => {
                        // Listen for sign-in state changes.
                        gapi.auth2.getAuthInstance().isSignedIn.listen(this.updateSigninStatus);

                        // Handle the initial sign-in state.
                        this.updateSigninStatus(gapi.auth2.getAuthInstance().isSignedIn.get());
                        // authorizeButton.onclick = handleAuthClick;
                        // signoutButton.onclick = handleSignoutClick;
                    },
                    (error) => {
                        // error.code (TODO, still valid?)
                        // error.message (TODO, still valid?)
                        // error.error
                        // error.details
                        if (GlobalConfig.DevMode || GlobalConfig.DebugMode) {
                            console.log("google classroom error:" + JSON.stringify(error), error);
                        }

                        let errorMsg = 'Error';
                        if (!!error.error) {
                            if (!!error.error.message) {
                                errorMsg = error.error.message;
                            }
                            else if (!!error.details) {
                                errorMsg = `Error: ${error.details}`;
                            }
                        }

                        this.setState({
                            showDialog: true, // stop being invisible
                            bodyText: errorMsg,
                        });
                    }
                );
        }
        
        /* old code
        if (typeof gapi === 'undefined' || typeof gapi.auth === 'undefined' || typeof gapi.auth.authorize === 'undefined') {
            console.log("waiting for gapi script to finish loading");
            // script hasn't loaded yet
            this.setState({
                showDialog: true, // stop being invisible
                bodyText: 'Loading script...'
            });
            window.setTimeout(this.checkAuth,1000);
            return;
        }
        gapi.auth.authorize({client_id: CLIENT_ID, scope: this.props.scope.join(' '), immediate: true},this.handleAuthResult);
        */
    }

    initClient() {
        GoogleClassroomActions.setGapiState(Globals.GAPI_LoadState.CLIENT_INITIALIZED);
    }
    
    updateSigninStatus(isSignedIn) {
        const explicitlySignedIn = isSignedIn && this.state._sign_in_clicked;
        // TODO: this setState gets called when assignment fails to create due to Forge server issue and modal is closed
        // ...causing "Warning: setState(...): Can only update a mounted or mounting component."
        this.setState({
            isSignedIn: isSignedIn,
            showAuthorizeButton: !explicitlySignedIn,
            showSignOutButton: explicitlySignedIn,
            showDialog: true, // stop being invisible
            bodyText: 'Identify and Authorize Google Classroom User',
        }, () => {
            if (isSignedIn && this.state._sign_in_clicked) {
                this.handleSuccess();
            }
        });
        
    }
    
    handleSignOut() {
        this.setState({
            showSignOutButton: false,
            showAuthorizeButton: false,
            bodyText: 'Signing out...'
            // isSignedIn: false,
        }, () => {
            GoogleClassroomActions.setSignInClickedState(false);
        });
    }
    
    // every google classroom user should at least have a user profile
    requestUserProfile() {
        const request = gapi.client.classroom.userProfiles.get({ userId : 'me' });
        request.execute((resp) => {
            if (resp.error) {
                if (GlobalConfig.DebugMode) {
                    console.log("requestUserProfile error: " + JSON.stringify(resp.error));
                }
                // if (resp.code === 403) {}

                this.setState({
                    showDialog: true,
                    showAuthorizeButton: false,
                    bodyText: "Unable to retrieve Google Classroom userProfile. Try signing in to google classroom if your author and google classroom emails are different.",
                    showRejectButton: true,
                    showAccountButton: true,
                    /***userProfile: {
                        name: {
                            givenName: '',
                            familyName: '',
                            fullName: '',
                    }***/
                });
                return;
            }

            this.setState({
                userProfile: resp.result
            }, () => {
                this.handleSuccess();
            });
        });
    }


    handleAuthClick(event) {
        this.setState(
            {
                bodyText: 'signing in...',
            },
            () => {
                GoogleClassroomActions.setSignInClickedState(true);
            }
        );
    }

    handleRejectClick() {
        gapi.auth2
            .getAuthInstance()
            .signOut();
        this.setState({
            showDialog: false,
            showAuthorizeButton: false,
            showRejectButton: false,
            showAccountButton: false,
            bodyText: 'Rejected.',
            isSignedIn: false,
        }, () => {
            this.props.onReject();
        });
    }

    handleSuccess() {
        if (!this.state.userProfile) {
            this.setState({
                bodyText: 'checking userProfile...'
            }, () => {
                this.requestUserProfile();
            });
            return;
        }

        this.setState({
            showDialog: false,
            showAuthorizeButton: false,
            showRejectButton: false,
            bodyText: 'Authorized.',
        }, () => {
            this.props.onSuccess();
        });
    }


    render() {
        // AccountChooser: https://accounts.google.com/AccountChooser
        // ServiceLogin: https://accounts.google.com/ServiceLogin?elo=1
        return (
            <Modal show={this.state.showDialog}>
                <Modal.Header>
                    <Modal.Title>Google Classroom Authorization</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.state.bodyText}
                </Modal.Body>
                <Modal.Footer>
                    {this.state.showAuthorizeButton && <Button variant='primary' onClick={this.handleAuthClick}>{this.state.authorizeButtonText}</Button>}
                    {this.state.showSignOutButton && <Button variant='primary' onClick={this.handleSignOut}>Sign Out</Button>}
                    {this.state.showRejectButton && <Button variant='warning' onClick={this.handleRejectClick}>Cancel</Button>}
                    {this.state.showAccountButton &&
                    <div className="gc-account-link">
                        <a href="https://classroom.google.com" target="_blank">Google Classroom</a>
                    </div>
                    }
                </Modal.Footer>
            </Modal>
        );
    }
}

Authinator.propTypes = propTypes;
Authinator.defaultProps = defaultProps;

export default Authinator;